import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { APP_CONFIG, AppConfig } from '../../config/app-config.token';
import { Subject, Subscription, debounceTime, filter, takeUntil, tap } from 'rxjs';
import { animate, style, transition, trigger } from '@angular/animations';
import { ThemeService } from '../../services/theme.service';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '../../services/auth.service';
import { AsyncPipe, NgClass } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { NavigationOption } from 'common';

@Component({
    selector: 'lib-header',
    standalone: true,
    imports: [NgClass, AsyncPipe, MatToolbarModule, MatButtonModule, MatListModule, MatIconModule, RouterModule],
    template: `
  
    @if(!authService.forceHideHeader && (authService.isAuthenticated$ | async)) {
    <mat-toolbar color="primary" class="d-flex flex-1-auto"
        [ngClass]="{'flex-just-between': themeService.isSmallDisplay}" [@inOutAnimation]="{
            value: ':enter',
            params: {
                height: themeService.isSmallDisplay ? '48px' : '64px',
            }
        }">
        <!-- Left Positioned by default. -->
        <div [ngClass]="{'flex-space-between h-100': themeService.isSmallDisplay, 'flex-left h-100': !themeService.isSmallDisplay}">
            <!-- Toggle main navigation -->
            <button mat-icon-button (click)="toggleDrawer.emit();" class="order-1" title="Open Menu">
                <mat-icon>menu</mat-icon>
            </button>

            <!-- Logo -->
            @if (appConfig?.logoSrc) {
            <img alt="Logo" class="mr-2 order-2 dynamic-height"
                [src]="appConfig!.logoSrc!"
                [style.opacity]="themeService.userTheme === 'dark-theme' ? '0.75' : '1'"
                [@inOutAnimation]="{
                    value: ':enter',
                    params: {
                        height: this.themeService.isSmallDisplay ? '48px' : '64px',
                }}"
            >
            }
        </div>

        <!-- Positioned to the center. Only for desktops. -->
        @if (!themeService.isSmallDisplay && (authService.isAuthenticated$ | async)) {
        <span class="flex-center order-3" cdkTrapFocus>
            @for (opt of navigationOptions; track opt; let idx = $index) {
            @if (opt.children) {
            <!-- Has embedded menus. The dropdown is a SIBLING of the trigger button,
                 never a child: a routerLink list item nested inside a <button> is
                 invalid interactive nesting and its clicks bubble into the toggle. -->
            <span class="nav-menu-anchor"
                (mouseenter)="openMenu(idx);"
                (mouseleave)="closeMenu(idx);"
            >
            <button mat-button type="button"
                [ngClass]="opt.headerClass"
                [attr.aria-expanded]="isMenuOpen(idx)"
                aria-haspopup="menu"
                (click)="isMenuOpen(idx) ? closeMenu(idx) : openMenu(idx);"
                class="nav-button-with-badge"
            >
                {{opt.title}}

                @if (opt.badgeCount && opt.badgeCount > 0) {
                    <span class="badge">{{opt.badgeCount}}</span>
                }

                @if (opt.hasNotification && (!opt.badgeCount || opt.badgeCount === 0)) {
                    <span class="notification-dot"></span>
                }
            </button>

            @if (isMenuOpen(idx)) {

            <div class="hover-menu-mouse-monitor"
                [@fadeInOut]
                [style.height]="(opt.children.length * 48) + 16 + 'px'"
            >
                <div class="notch"></div>
                <mat-list>
                @for (child of opt.children; track child) {
                <mat-list-item [routerLink]="child.path"  [queryParams]="child.queryParam"
                        class="small-nav" [ngClass]="{'active-nav': child.isCurrentNav}" [title]="child.title" >
                    <mat-icon matListItemIcon>{{child.icon}}</mat-icon>
                    <div matListItemTitle>{{child.title}}</div>
                </mat-list-item>
                }
                </mat-list>
            </div>
            }
            </span>
            } @else {
            <button mat-button type="button" [title]="opt.title" [ngClass]="opt.headerClass" [ngClass]="{'active-nav': opt.isCurrentNav}" [routerLink]="opt.path" [queryParams]="opt.queryParam">
                <mat-icon>{{opt.icon}}</mat-icon>{{opt.title}}</button>
            }
            }
        </span>
        }

        <!-- Positioned to the right. For desktops. -->
        @if (!themeService.isSmallDisplay) {
        <section class="flex-right order-4">

            @if (authService.isAuthenticated$ | async) {
            <!-- Logout button -->
            <button mat-button (click)="authService.logout()" title="Logout">Sign out<mat-icon iconPositionEnd>logout</mat-icon></button>
            } @else {
            <!-- Login button -->
            <button mat-button routerLink="/login" title="Login">Sign in<mat-icon iconPositionEnd>login</mat-icon></button>
            }
        </section>
        }
    </mat-toolbar>
    }
  `,
    styles: `
        .title {
            order: 2;
        }

        // Positioning context for the dropdown, which is now a sibling of the
        // trigger button rather than nested inside it.
        .nav-menu-anchor {
            position: relative;
            display: inline-flex;
            align-items: center;
        }

        .flex-center {
            button {
                margin-right: 0.5rem;

                &.nav-button-with-badge {
                    position: relative;

                    .badge {
                        position: absolute;
                        top: -7.5px;
                        right: -7.5px;
                        background-color: #f44336;
                        border-radius: 10px;
                        padding: 5px 5px;
                        font-size: 10px;
                        min-width: 10px;
                        line-height: 1;
                    }

                    .notification-dot {
                        position: absolute;
                        top: -3px;
                        right: -3px;
                        width: 8px;
                        height: 8px;
                        background-color: #f44336;
                        border-radius: 50%;
                    }
                }
            }
        }

        .hide-at-840 {
            @media screen and (max-width: 840px) {
                display: none;
            }
        }

        .dynamic-height {
            height: 64px;

            @media screen and (max-width: 600px) {
                height: 48px;
            }
        }

        button.active-nav {
            font-weight: bold;
        }

        // For small displays, reduce the title size.
        @media screen and (max-width: 600px) {
            // .title {
            //     font-size: 0.9em;
            //     order: 3;
            // }
        }

        .hover-menu-mouse-monitor {
            position: absolute;
            left: -2rem;
            top: 2.1rem; // TODO: make this 2rem and add notch.
            width: 20rem;
            height: auto;
            background-color: white;
            border: 1px solid #9e9e9e;
            border-radius: 4px;
            z-index: 2;
            
            .notch {
                position: absolute;
                top: -0.5rem;
                left: 2rem;
                width: 0;
                height: 0;
                z-index: 1;
                border-left: 0.5rem solid transparent;
                border-right: 0.5rem solid transparent;
                border-bottom: 0.5rem solid #9e9e9e;
            }
        }

        ::ng-deep {

            .small-nav {
                height: 3rem !important;
                line-height: 2.5rem;
                flex-wrap: wrap;
                text-align: left;

                &:hover {
                    cursor: pointer;
                    background-color: #f5f5f5;
                }

                &.active-nav {
                    background-color: #e5e5e5;
                }

                mat-icon {
                    font-size: 1.25rem;
                    line-height: 1.25rem;
                    height: 1.25rem;
                    width: 1.25rem;
                    margin-right: 1rem !important;
                    margin-left: 1rem !important;
                }
            }

            .dark-theme {

                .small-nav {
                    &:hover {
                        background-color: #424242;
                    }

                    &.active-nav {
                        background-color: #656565;
                    }
                }

                .hover-menu-mouse-monitor {
                    border: 1px solid #ccc;
                    background-color: #424242;

                    .notch {
                        border-bottom: 0.5rem solid #ccc !important;
                    }
                }

                .fixed-bottom {
                    background: #424242;
                }

                mat-list mat-list-item:hover {
                    background-color: #898989 !important;
                }
            }
        }
    `,
    animations: [
        trigger(
            'inOutAnimation',
            [
                transition(
                    ':enter', [
                    style({ height: 0, opacity: 0 }),
                    animate('0.3s ease-out',
                        style({ height: '{{height}}', opacity: 1 }))
                ]),
                transition(
                    ':leave', [
                    style({ height: '{{height}}', opacity: 1 }),
                    animate('0.3s ease-in',
                        style({ height: 0, opacity: 0 }))
                ])
            ]
        ),
        trigger(
            'fadeInOut',
            [
                transition(
                    ':enter', [
                    style({ opacity: 0 }),
                    animate('300ms ease-out',
                        style({ opacity: 1 }))
                ]),
                transition(
                    ':leave', [
                    style({ opacity: 1 }),
                    animate('200ms ease-in',
                        style({ opacity: 0 }))
                ])
            ]
        )
    ]
})
export class HeaderComponent implements OnInit, OnDestroy {

    @Input()
    public title = '';

    @Input()
    public navigationOptions: NavigationOption[] = [];

    protected readonly appConfig: AppConfig | null = inject(APP_CONFIG, { optional: true });

    @Output()
    public toggleDrawer = new EventEmitter<void>();

    /**
     * Menu state for each navigation option.
     * isOpen will show/hide the menu.
     * The observer will be used to open and close the menu. 
     * - When closing, there is a 300ms delay.
     * - When opening, close all others and opens immediately.
     *
     * @type {(Record<number, {
     *         isOpen: boolean, sub: Subscription,
     *         observer: Subject<{
     *             order: "toggle" | "close" | "open";
     *             index: number;
     *         }>
     *     }>)}
     * @memberof HeaderComponent
     */
    public menuState: Record<number, {
        isOpen: boolean, sub: Subscription,
        observer: Subject<{
            order: "toggle" | "close" | "open";
            index: number;
        }>
    }> = {};

    public authService = inject(AuthService);
    public themeService = inject(ThemeService);
    private readonly _router = inject(Router);
    private destroy$ = new Subject<void>();

    public ngOnInit(): void {
        this._router.events.pipe(
            filter(e => e instanceof NavigationEnd),
            takeUntil(this.destroy$),
        ).subscribe(() => Object.values(this.menuState).forEach(m => m.isOpen = false));

        this.navigationOptions.forEach((_, index) => {
            this.menuState[index] = {
                isOpen: false,
                sub: null as any,
                observer: new Subject<{ order: 'toggle' | 'close' | 'open', index: number }>()
            }

            this.menuState[index].sub = this.menuState[index].observer
                .pipe(
                    // Open should happen immediately.
                    tap(state => {
                        if (state.order === 'open') {
                            // Close all others immediately.
                            Object.keys(this.menuState).forEach(key => {
                                if (key !== index.toString()) {
                                    this.menuState[+key].isOpen = false;
                                }
                            });

                            // Open current one.
                            this.menuState[state.index].isOpen = true;
                        }
                    }),
                    // Close should happen after 300ms.
                    debounceTime(200),
                    takeUntil(this.destroy$)
                )
                .subscribe(state => { this.menuState[state.index].isOpen = state.order !== 'close'; }
                );
        });
    }

    /**
     * Safe read for the template. `menuState` is built once from the
     * `navigationOptions` present at init; a consumer that *replaces* the array
     * (rather than mutating it) would otherwise leave `menuState[idx]` undefined
     * and throw on every change-detection pass.
     */
    public isMenuOpen(index: number): boolean {
        return this.menuState[index]?.isOpen === true;
    }

    public openMenu(index: number) {
        this.menuState[index]?.observer.next({ index, order: 'open' });
    }

    public closeMenu(index: number) {
        this.menuState[index]?.observer.next({ index, order: 'close' });
    }


    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
